import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getLocalizedText } from '../cms/localization';
import type { SupportedLang } from '../cms/localization';
import type { LocalizedText } from '../../shared/types';

interface StepItem {
  title: LocalizedText;
  desc: LocalizedText;
}

interface PainCardItem {
  text: LocalizedText;
  tip: LocalizedText;
}

interface TechSynergySectionProps {
  content: Record<string, unknown>;
}

const stepIcons = [
  // 01 碳纖維加熱
  <svg key="1" viewBox="0 0 40 40" fill="none" className="w-9 h-9">
    <path d="M6 30 Q10 26 14 30 T22 30 T30 30 T34 30" stroke="#A6392A" strokeWidth="2.4" strokeLinecap="round" />
    <path className="heatline h1" d="M12 24 Q10 18 12 12" stroke="#C69A46" strokeWidth="2" strokeLinecap="round" />
    <path className="heatline h2" d="M20 24 Q18 16 20 8" stroke="#C69A46" strokeWidth="2" strokeLinecap="round" />
    <path className="heatline h3" d="M28 24 Q26 18 28 12" stroke="#C69A46" strokeWidth="2" strokeLinecap="round" />
  </svg>,
  // 02 砭石升溫
  <svg key="2" viewBox="0 0 40 40" fill="none" className="w-9 h-9">
    <ellipse className="stone-ring r1" cx="20" cy="20" rx="16" ry="16" stroke="#EAD9AE" strokeWidth="1.5" />
    <ellipse className="stone-ring r2" cx="20" cy="20" rx="12" ry="12" stroke="#C69A46" strokeWidth="1.5" />
    <path d="M12 24c0-6 4-10 8-10s8 4 8 10-4 6-8 6-8 0-8-6z" fill="#A6392A" opacity="0.9" />
  </svg>,
  // 03 遠紅外線＋超聲波
  <svg key="3" viewBox="0 0 40 40" fill="none" className="w-9 h-9">
    <path className="wave wave-a" d="M2 18 Q6 10 10 18 T18 18 T26 18" stroke="#A6392A" strokeWidth="2" strokeLinecap="round" />
    <path className="wave wave-b" d="M2 24 Q6 28 10 24 T18 24 T26 24" stroke="#C69A46" strokeWidth="1.6" strokeLinecap="round" opacity="0.85" />
    <path d="M29 6 Q35 20 29 34" stroke="#7A6A54" strokeWidth="1.6" fill="none" strokeDasharray="1 5" strokeLinecap="round" />
    <circle className="meridian-dot md1" cx="29" cy="6" r="1.7" fill="#A6392A" />
    <circle className="meridian-dot md2" cx="31.5" cy="20" r="1.7" fill="#A6392A" />
    <circle className="meridian-dot md3" cx="29" cy="34" r="1.7" fill="#A6392A" />
  </svg>,
  // 04 紅光照射
  <svg key="4" viewBox="0 0 40 40" fill="none" className="w-9 h-9">
    <path d="M20 4 L10 30 L30 30 Z" fill="#A6392A" opacity="0.12" />
    <line className="ray-line" x1="20" y1="4" x2="20" y2="19" stroke="#A6392A" strokeWidth="2" strokeLinecap="round" />
    <circle className="cell c1" cx="14" cy="28" r="3" fill="#C69A46" />
    <circle className="cell c2" cx="20" cy="30" r="3.2" fill="#A6392A" />
    <circle className="cell c3" cx="26" cy="28" r="3" fill="#C69A46" />
  </svg>,
];

const painIcons = [
  // 肩頸酸痛
  <svg key="1" viewBox="0 0 24 24" fill="none" className="w-5 h-5">
    <circle cx="12" cy="6" r="3" stroke="#A6392A" strokeWidth="1.6" />
    <path d="M6 21c0-4 3-7 6-7s6 3 6 7" stroke="#A6392A" strokeWidth="1.6" strokeLinecap="round" />
    <circle cx="12" cy="13" r="1.3" fill="#C69A46" />
  </svg>,
  // 手腳冰冷
  <svg key="2" viewBox="0 0 24 24" fill="none" className="w-5 h-5">
    <path d="M12 2v20M12 2l-2.5 2.5M12 2l2.5 2.5M12 22l-2.5-2.5M12 22l2.5-2.5M2 12h20M2 12l2.5-2.5M2 12l2.5 2.5M22 12l-2.5-2.5M22 12l-2.5 2.5" stroke="#A6392A" strokeWidth="1.4" strokeLinecap="round" />
  </svg>,
  // 疲勞不適
  <svg key="3" viewBox="0 0 24 24" fill="none" className="w-5 h-5">
    <rect x="3" y="8" width="16" height="8" rx="1.5" stroke="#A6392A" strokeWidth="1.6" />
    <rect x="20" y="10.5" width="2" height="3" fill="#A6392A" />
    <rect x="5" y="10" width="4" height="4" fill="#C69A46" />
  </svg>,
  // 五十肩 / 關節
  <svg key="4" viewBox="0 0 24 24" fill="none" className="w-5 h-5">
    <circle cx="12" cy="12" r="3" stroke="#A6392A" strokeWidth="1.6" />
    <path d="M12 3a9 9 0 0 1 8 4.8" stroke="#C69A46" strokeWidth="1.6" strokeLinecap="round" />
    <path d="M12 21a9 9 0 0 1-8-4.8" stroke="#C69A46" strokeWidth="1.6" strokeLinecap="round" />
  </svg>,
  // 睡眠品質差
  <svg key="5" viewBox="0 0 24 24" fill="none" className="w-5 h-5">
    <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" stroke="#A6392A" strokeWidth="1.6" strokeLinejoin="round" />
  </svg>,
];

function parseStepFromString(text: string): { title: string; desc: string } {
  const sep = text.includes('・') ? '・' : text.includes(' · ') ? ' · ' : text.includes(' - ') ? ' - ' : '';
  if (!sep) return { title: text, desc: '' };
  const [title, ...rest] = text.split(sep);
  return { title: title.trim(), desc: rest.join(sep).trim() };
}

function parseResults(text: string): string[] {
  return text
    .split(/→|->/)
    .map((s) => s.trim())
    .filter(Boolean);
}

const TechSynergySection: React.FC<TechSynergySectionProps> = ({ content }) => {
  const { i18n } = useTranslation();
  const lang = i18n.language as SupportedLang;

  const eyebrow = getLocalizedText(content.synergyEyebrow, lang, '扶陽堂 · 科技解構');
  const title = getLocalizedText(content.synergyTitle, lang, getLocalizedText(content.flowTitle, lang, '三種科技如何協同作用？'));
  const subtitle = getLocalizedText(content.synergySubtitle, lang, '由熱能到光能，四道工序層層遞進，把溫度化成看得見的舒緩');

  const steps = useMemo<StepItem[]>(() => {
    const raw = content.synergySteps as StepItem[] | undefined;
    if (Array.isArray(raw) && raw.length > 0) return raw;
    const flow = (content.flow as LocalizedText[] | undefined) || [];
    return flow.map((item) => {
      const text = getLocalizedText(item, lang, '');
      const { title: t, desc: d } = parseStepFromString(text);
      return { title: { 'zh-HK': t, 'zh-CN': t, en: t }, desc: { 'zh-HK': d, 'zh-CN': d, en: d } };
    });
  }, [content.synergySteps, content.flow, lang]);

  const results = useMemo<string[]>(() => {
    const raw = content.synergyResults as LocalizedText[] | undefined;
    if (Array.isArray(raw) && raw.length > 0) return raw.map((r) => getLocalizedText(r, lang, ''));
    const text = getLocalizedText(content.flowResult, lang, '');
    return parseResults(text);
  }, [content.synergyResults, content.flowResult, lang]);

  const painTitle = getLocalizedText(content.painTitle, lang, getLocalizedText(content.benefitsTitle, lang, '告別這些困擾，從提升體溫開始'));
  const painCta = getLocalizedText(content.painCta, lang, '立即了解四大科技如何守護你 →');

  const painCards = useMemo<PainCardItem[]>(() => {
    const raw = content.painCards as PainCardItem[] | undefined;
    if (Array.isArray(raw) && raw.length > 0) return raw;
    const benefits = (content.benefits as LocalizedText[] | undefined) || [];
    const tips = (content.painTips as LocalizedText[] | undefined) || [];
    return benefits.map((b, i) => ({ text: b, tip: tips[i] || { 'zh-HK': '', 'zh-CN': '', en: '' } }));
  }, [content.painCards, content.benefits, content.painTips]);

  const [activeResult, setActiveResult] = useState(0);

  useEffect(() => {
    if (results.length === 0) return;
    const id = setInterval(() => {
      setActiveResult((prev) => (prev + 1) % results.length);
    }, 1400);
    return () => clearInterval(id);
  }, [results.length]);

  return (
    <>
      <style>{`
        .heatline { transform-origin: center; animation: rise 1.8s ease-in-out infinite; }
        .h2 { animation-delay: .25s; }
        .h3 { animation-delay: .5s; }
        @keyframes rise {
          0% { opacity: .25; transform: translateY(3px); }
          50% { opacity: 1; }
          100% { opacity: 0; transform: translateY(-5px); }
        }
        .stone-ring { transform-origin: 20px 20px; animation: ripple 2.4s ease-out infinite; }
        .r2 { animation-delay: .7s; }
        @keyframes ripple {
          0% { opacity: .9; transform: scale(.7); }
          100% { opacity: 0; transform: scale(1.25); }
        }
        .wave { transform-origin: center; animation: sway 2.2s ease-in-out infinite; }
        .wave-b { animation-delay: .3s; }
        @keyframes sway {
          0%, 100% { opacity: .5; transform: translateX(0); }
          50% { opacity: 1; transform: translateX(2px); }
        }
        .meridian-dot { animation: dotGlow 1.8s ease-in-out infinite; }
        .md2 { animation-delay: .3s; }
        .md3 { animation-delay: .6s; }
        @keyframes dotGlow {
          0%, 100% { opacity: .35; }
          50% { opacity: 1; }
        }
        .cell { transform-origin: center; animation: pulseCell 1.6s ease-in-out infinite; }
        .c2 { animation-delay: .25s; }
        .c3 { animation-delay: .5s; }
        @keyframes pulseCell {
          0%, 100% { transform: scale(1); opacity: .75; }
          50% { transform: scale(1.25); opacity: 1; }
        }
        .rail-flow::after {
          content: '';
          position: absolute; inset: 0;
          background: linear-gradient(to bottom, transparent 0%, #C69A46 35%, #A6392A 55%, #C69A46 75%, transparent 100%);
          background-size: 100% 260%;
          animation: railFlow 3.6s linear infinite;
        }
        @keyframes railFlow {
          0% { background-position: 0 -160%; }
          100% { background-position: 0 100%; }
        }
        @media (prefers-reduced-motion: reduce) {
          .heatline, .stone-ring, .wave, .meridian-dot, .cell, .rail-flow::after { animation: none !important; }
        }
      `}</style>

      {/* 三種科技如何協同作用 */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8 md:py-12">
        <p className="text-center text-brand-gold text-xs tracking-[0.28em] font-bold mb-3">{eyebrow}</p>
        <h2 className="text-center font-serif text-2xl md:text-3xl text-brand-ink mb-3">
          {title}
        </h2>
        <p className="text-center text-brand-ink/60 text-sm mb-12">{subtitle}</p>

        <div className="relative pl-20 md:pl-24 mb-10">
          {/* rail */}
          <div className="rail-flow absolute top-9 bottom-9 left-8 md:left-9 w-0.5 bg-brand-gold/25 rounded-full overflow-hidden" aria-hidden="true" />

          {steps.map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.6, delay: i * 0.13 }}
              className="relative flex items-start gap-5 mb-6 last:mb-0 group"
            >
              <div className="flex-shrink-0 w-[68px] h-[68px] -ml-20 md:-ml-24 rounded-full bg-white border-2 border-brand-gold/30 flex items-center justify-center shadow-sm relative">
                <span className="absolute -top-1.5 -left-1.5 w-6 h-6 rounded-full bg-brand-cinnabar text-white text-xs font-serif flex items-center justify-center">
                  {String(i + 1).padStart(2, '0')}
                </span>
                {stepIcons[i] || stepIcons[0]}
              </div>
              <div className="flex-1 bg-white border border-brand-gold/20 rounded-xl px-5 py-4 transition-all group-hover:border-brand-gold group-hover:shadow-lg group-hover:translate-x-0.5">
                <h3 className="font-serif text-lg text-brand-ink mb-1">{getLocalizedText(step.title, lang)}</h3>
                <p className="text-brand-ink/60 text-[13.5px]">{getLocalizedText(step.desc, lang)}</p>
              </div>
            </motion.div>
          ))}
        </div>

        {/* result banner */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-brand-cinnabar to-[#832C20] rounded-xl px-5 py-5 flex flex-wrap items-center justify-center gap-3 md:gap-4 shadow-xl"
        >
          {results.map((item, i) => (
            <React.Fragment key={i}>
              <span
                className={`font-serif font-bold text-base md:text-lg tracking-wide px-3 py-1.5 rounded-lg transition-all duration-500 ${
                  activeResult === i ? 'text-white bg-white/15 scale-105' : 'text-white/70'
                }`}
              >
                {item}
              </span>
              {i < results.length - 1 && <span className="text-white/50 text-sm">→</span>}
            </React.Fragment>
          ))}
        </motion.div>
      </div>

      {/* 告別這些困擾 */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-10 md:pb-14">
        <h2 className="text-center font-serif text-2xl md:text-3xl text-brand-ink mb-10">
          {painTitle}
        </h2>

        <div className="flex flex-wrap justify-center gap-4 mb-10">
          {painCards.map((card, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08 }}
              tabIndex={0}
              className="group flex-[1_1_200px] max-w-[230px] bg-white border border-brand-gold/20 rounded-xl p-5 text-center transition-all hover:-translate-y-1 hover:shadow-xl hover:border-brand-gold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-cinnabar"
            >
              <div className="w-10 h-10 mx-auto mb-3 rounded-full bg-brand-stone/60 flex items-center justify-center transition-all group-hover:bg-brand-gold/20 group-hover:rotate-[-6deg] group-hover:scale-110">
                {painIcons[i] || painIcons[0]}
              </div>
              <p className="text-brand-ink/80 text-[13.5px] leading-relaxed">{getLocalizedText(card.text, lang)}</p>
              {getLocalizedText(card.tip, lang) && (
                <p className="mt-2 text-brand-cinnabar text-xs font-bold opacity-0 max-h-0 overflow-hidden transition-all group-hover:opacity-100 group-hover:max-h-10 group-focus:opacity-100 group-focus:max-h-10">
                  {getLocalizedText(card.tip, lang)}
                </p>
              )}
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <a
            href="#principle"
            className="inline-block font-sans font-bold text-sm text-white bg-brand-cinnabar rounded-full px-8 py-3.5 shadow-lg hover:bg-brand-ink hover:-translate-y-0.5 transition-all"
          >
            {painCta}
          </a>
        </div>
      </div>
    </>
  );
};

export default TechSynergySection;
